import { useState, useContext, useEffect } from 'react'
import styles from './MembersTable.module.css'
import { SplitViewContext, DataRefreshContext } from './ManageSection'
import { AddMemberModal } from './modals'
import { MemberDetails } from './details'
import apiClient from '../utils/api'
import { formatDisplayDate } from '../utils/dateUtils'

export default function MembersTable({ apiToken, user, selectedMemberId, onSubSectionChange }) {
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('active')
    const [roleFilter, setRoleFilter] = useState('all')
    const [showAddModal, setShowAddModal] = useState(false)
    const [selectedMember, setSelectedMember] = useState(null)
    const [members, setMembers] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [availableFilters, setAvailableFilters] = useState({ statuses: [], roles: [] })
    const [successMessage, setSuccessMessage] = useState('')
    
    // Get split view context
    const splitViewContext = useContext(SplitViewContext)
    const { setIsInSplitView } = splitViewContext || {}

    // Get data refresh context
    const dataRefreshContext = useContext(DataRefreshContext)
    const { refreshKey } = dataRefreshContext || {}

    // Update split view state when member selection changes
    useEffect(() => {
        if (setIsInSplitView) {
            setIsInSplitView(!!selectedMember)
        }
    }, [selectedMember, setIsInSplitView])

    // Listen for global refresh events
    useEffect(() => {
        if (refreshKey > 0 && apiToken) {
            console.log('[MembersTable] Detected refresh event, reloading members...')
            loadMembers()
        }
    }, [refreshKey])

    // Load members data from API
    useEffect(() => {
        if (!apiToken) return
        
        loadMembers()
    }, [apiToken, searchTerm, statusFilter, roleFilter])

    // Handle selectedMemberId prop changes (from URL)
    useEffect(() => {
        if (selectedMemberId && members.length > 0) {
            const member = members.find(m => m.id === selectedMemberId)
            if (member) {
                setSelectedMember(member)
            }
        } else if (!selectedMemberId) {
            setSelectedMember(null)
        }
    }, [selectedMemberId, members])

    const matchesCurrentFilters = (member) => {
        if (!member) return false

        const matchesStatus = statusFilter === 'all' || member.status === statusFilter
        const matchesRole = roleFilter === 'all' || member.role === roleFilter

        const term = searchTerm.trim().toLowerCase()
        const matchesSearch = !term || [
            member.full_name,
            member.display_name,
            member.email
        ].some(value => value && value.toLowerCase().includes(term))

        return matchesStatus && matchesRole && matchesSearch
    }

    const loadMembers = async () => {
        try {
            setLoading(true)
            setError(null)
            
            apiClient.setAuthToken(apiToken)
            const params = {}
            
            if (searchTerm.trim()) {
                params.search = searchTerm.trim()
            }
            if (statusFilter !== 'all') {
                params.status = statusFilter
            }
            if (roleFilter !== 'all') {
                params.role = roleFilter
            }
            
            const response = await apiClient.getTeammates(params)
            setMembers(response.teammates || [])
            setAvailableFilters(response.filters || { statuses: [], roles: [] })
        } catch (err) {
            console.error('Failed to load members:', err)
            setError('Failed to load team members. Please try again.')
        } finally {
            setLoading(false)
        }
    }


    const handleMemberSave = (newMember, message) => {
        if (matchesCurrentFilters(newMember)) {
            setMembers(prevMembers => {
                const existingIndex = prevMembers.findIndex(member => member.id === newMember.id)
                if (existingIndex >= 0) {
                    const updatedMembers = [...prevMembers]
                    updatedMembers[existingIndex] = newMember
                    return updatedMembers
                }

                return [newMember, ...prevMembers]
            })
        } else {
            // Refresh list so the new member appears when filters allow it
            loadMembers()
        }
        
        // Show success message
        setSuccessMessage(message || 'Member added successfully')
        
        // Clear success message after 5 seconds
        setTimeout(() => {
            setSuccessMessage('')
        }, 5000)
    }


    // Members are already filtered by the API, so we can use them directly
    const filteredMembers = members

    const getStatusBadge = (status) => {
        const statusClass = {
            'active': styles.statusActive,
            'inactive': styles.statusInactive,
            'pending': styles.statusPending,
            'suspended': styles.statusSuspended
        }[status] || styles.statusActive

        const statusText = {
            'active': 'Active',
            'inactive': 'Inactive',
            'pending': 'Pending',
            'suspended': 'Suspended'
        }[status] || status

        return (
            <span className={`${styles.statusBadge} ${statusClass}`}>
                {statusText}
            </span>
        )
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Never'
        return formatDisplayDate(dateString)
    }

    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0)?.toUpperCase() || ''
        const last = lastName?.charAt(0)?.toUpperCase() || ''
        return first + last
    }

    const capitalizeWord = (word) => {
        if (!word) return ''
        return word.charAt(0).toUpperCase() + word.slice(1)
    }

    const handleMemberSelect = (member) => {
        setSelectedMember(member)
        
        // Update URL to include member ID
        if (onSubSectionChange) {
            onSubSectionChange('members', member.id)
        }
    }

    const handleCloseDetails = () => {
        setSelectedMember(null)
        
        // Update URL to remove member ID
        if (onSubSectionChange) {
            onSubSectionChange('members')
        }
    }

    const handleSuccessMessage = (message) => {
        setSuccessMessage(message)
        setTimeout(() => setSuccessMessage(''), 5000)
    }

    const handleError = (errorMessage) => {
        setError(errorMessage)
    }

    const handleMemberRemoved = (removedMember) => {
        // Remove the member from the local state
        setMembers(prevMembers => prevMembers.filter(member => member.id !== removedMember.id))
        
        // Close the details panel
        setSelectedMember(null)
        
        // Update URL to remove member ID
        if (onSubSectionChange) {
            onSubSectionChange('members')
        }
    }

    const handleMemberUpdated = (updatedMember) => {
        const memberMatchesFilters = matchesCurrentFilters(updatedMember)

        setMembers(prevMembers => {
            const existingIndex = prevMembers.findIndex(member => member.id === updatedMember.id)

            if (!memberMatchesFilters) {
                if (existingIndex === -1) return prevMembers
                return prevMembers.filter(member => member.id !== updatedMember.id)
            }

            if (existingIndex === -1) {
                return [updatedMember, ...prevMembers]
            }

            const updatedMembers = [...prevMembers]
            updatedMembers[existingIndex] = updatedMember
            return updatedMembers
        })

        if (memberMatchesFilters) {
            setSelectedMember(prevSelected => {
                if (prevSelected?.id === updatedMember.id) {
                    return updatedMember
                }
                return prevSelected
            })
        } else if (selectedMember?.id === updatedMember.id) {
            handleCloseDetails()
        }
    }

    return (
        <div className={`${styles.membersContainer} ${selectedMember ? styles.split : ''}`}>
            <div className={`${styles.splitView} ${selectedMember ? styles.split : ''}`}>
                {/* Main Members Content */}
                <div className={selectedMember ? styles.membersSidebar : styles.membersMain}>
                    {!selectedMember && (
                        <div className={styles.header}>
                            <div className={styles.headerLeft}>
                                <h2>Team Members</h2>
                                <p>Manage team members and their permissions</p>
                            </div>
                            <button 
                                className={styles.addButton}
                                onClick={() => setShowAddModal(true)}
                            >
                                + Add Member
                            </button>
                        </div>
                    )}

                    {/* Conditional Content: Show full table or simple list based on selection */}
                    {!selectedMember ? (
                        <>
                            {/* Search and Filters */}
                            <div className={styles.controls}>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className={styles.filterSelect}
                                    disabled={loading}
                                >
                                    <option value="all">All Status</option>
                                    {availableFilters.statuses.map(status => (
                                        <option key={status} value={status}>{capitalizeWord(status)}</option>
                                    ))}
                                </select>
                                <select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    className={styles.filterSelect}
                                    disabled={loading}
                                >
                                    <option value="all">All Roles</option>
                                    {availableFilters.roles.map(role => (
                                        <option key={role} value={role}>{capitalizeWord(role)}</option>
                                    ))}
                                </select>
                                <div className={styles.searchBox}>
                                    <input
                                        type="text"
                                        placeholder="Search members..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={styles.searchInput}
                                        disabled={loading}
                                    />
                                    <span className={styles.searchIcon}>🔍</span>
                                </div>
                            </div>

                            {/* Success Message */}
                            {successMessage && (
                                <div className={styles.successMessage}>
                                    <p>{successMessage}</p>
                                </div>
                            )}

                            {/* Loading/Error States */}
                            {loading && (
                                <div className={styles.loadingState}>
                                    <p>Loading team members...</p>
                                </div>
                            )}

                            {error && (
                                <div className={styles.errorState}>
                                    <p>{error}</p>
                                    <button onClick={loadMembers} className={styles.retryButton}>
                                        Retry
                                    </button>
                                </div>
                            )}

                            {/* Members Table */}
                            {!loading && !error && (
                                <div className={styles.tableContainer}>
                                    <table className={styles.membersTable}>
                                        <thead>
                                            <tr>
                                                <th>Member</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>Status</th>
                                                <th>Last Sign In</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredMembers.length > 0 ? (
                                                filteredMembers.map(member => (
                                                    <tr 
                                                        key={member.id}
                                                        onClick={() => handleMemberSelect(member)}
                                                        className={styles.tableRow}
                                                    >
                                                        <td>
                                                            <div className={styles.memberCell}>
                                                                <div className={styles.memberAvatar}>
                                                                    {member.avatar_url ? (
                                                                        <img src={member.avatar_url} alt={member.full_name} />
                                                                    ) : (
                                                                        <div className={styles.avatarPlaceholder}>
                                                                            {getInitials(member.first_name, member.last_name)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className={styles.memberInfo}>
                                                                    <span className={styles.name}>{member.full_name}</span>
                                                                    <span className={styles.description}>{member.display_name}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{member.email}</td>
                                                        <td>{capitalizeWord(member.role)}</td>
                                                        <td>{getStatusBadge(member.status)}</td>
                                                        <td>{formatDate(member.last_sign_in_at)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className={styles.noResults}>
                                                        No members found matching your criteria
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    ) : (
                        /* Simple Members List for Split View */
                        <div className={styles.membersListContainer}>
                            <div className={styles.listHeader}>
                                <h3>Members</h3>
                                <div className={styles.listHeaderRight}>
                                    <span className={styles.memberCount}>{filteredMembers.length} members</span>
                                    <button 
                                        className={styles.addButtonCompact}
                                        onClick={() => setShowAddModal(true)}
                                        aria-label="Add Member"
                                        title="Add Member"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <div className={styles.membersList}>
                                {filteredMembers.map(member => (
                                    <div 
                                        key={member.id}
                                        className={`${styles.memberListItem} ${member.id === selectedMember.id ? styles.active : ''}`}
                                        onClick={() => handleMemberSelect(member)}
                                    >
                                        <div className={styles.memberListAvatar}>
                                            {member.avatar_url ? (
                                                <img src={member.avatar_url} alt={member.full_name} />
                                            ) : (
                                                <div className={styles.avatarPlaceholder}>
                                                    {getInitials(member.first_name, member.last_name)}
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.memberListInfo}>
                                            <div className={styles.memberListName}>{member.full_name}</div>
                                            <div className={styles.memberListRole}>{capitalizeWord(member.role)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Member Details Panel */}
                {selectedMember && (
                    <MemberDetails
                        member={selectedMember}
                        onClose={handleCloseDetails}
                        apiToken={apiToken}
                        onSuccessMessage={handleSuccessMessage}
                        onError={handleError}
                        onMemberRemoved={handleMemberRemoved}
                        onMemberUpdate={handleMemberUpdated}
                    />
                )}
            </div>

            {/* Add Member Modal */}
            <AddMemberModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSave={handleMemberSave}
                apiToken={apiToken}
                availableRoles={availableFilters.roles.length > 0 ? availableFilters.roles : ['member', 'admin']}
            />
        </div>
    )
}
